// Builds the "review changes" list for the Edit Merchant form: every field the admin
// actually changed, old → new, grouped by the form's own section headings.
//
// It mirrors what handleSubmit sends, not just what the inputs show. A field whose
// blank value the server ignores (Deliverect credentials, currency, secrets) is only a
// change when a new value is typed, so the preview never promises an edit that the save
// would silently drop. Secret values are never shown, only "New value set" / "Will be removed".

const ON_OFF = (v) => (v ? "On" : "Off");
const EMPTY = "—";

// Sections that hold credentials or decide where money goes. Their rows get a
// "Sensitive" tag in the preview.
export const SENSITIVE_SECTIONS = new Set([
  "Deliverect Integration",
  "Koinz Integration",
  "Blend Integration",
  "Google Maps",
  "Payment Gateway",
  "Moyasar Payment",
  "Telr Payment",
]);

// What clearing a secret actually does at runtime, shown under its "Will be removed" row.
const REMOVE_NOTES = {
  googleMapsApiKey:
    "Distances fall back to the platform key (GOOGLE_MAPS_SERVER_KEY) if one is set; otherwise branches are ordered by straight-line distance with no distance label.",
  moyasarSecretKey:
    "There is no global fallback key: payment verification, refunds and wallet top-up checks for this merchant will fail.",
  moyasarWebhookSecret:
    "Webhooks signed with this merchant's own token will be rejected; their payments are left to the reconcile jobs.",
  koinzPosId:
    "Koinz orders for this merchant can no longer be matched to it and will be rejected. The cached Koinz token is dropped.",
  koinzAccessKey:
    "Calls to Koinz for this merchant fail until a new key is set. The cached Koinz token is dropped.",
  koinzMerchantKey:
    "Calls to Koinz for this merchant fail until a new key is set. The cached Koinz token is dropped.",
  blendMerchantPassword:
    "Menu reads for Blend stop until a new password is set. Blend's already-issued token is not revoked.",
};

// type:
//   bool | number | text | color | select → always submitted, compared after normalizing
//   keepIfBlank → the server ignores a blank value, so only a new non-blank value counts
//   secret      → loads blank; typed value = replace, clearSecrets = remove
//   image       → a picked File = replaced; removable ones can also go to ""
const FIELDS = [
  { section: "Merchant Image", key: "image1", label: "Merchant Image", type: "image", fileKey: "image1" },
  { section: "Merchant Image", key: "favicon", label: "Favicon", type: "image", fileKey: "favicon" },

  { section: "Merchant Details", key: "customer", label: "Customer", type: "select" },
  { section: "Merchant Details", key: "slug", label: "URL Slug (public pages)", type: "text" },
  { section: "Merchant Details", key: "url", label: "URL", type: "text" },
  { section: "Merchant Details", key: "db", label: "Database", type: "text" },
  { section: "Merchant Details", key: "email", label: "Odoo Email", type: "keepIfBlank" },
  { section: "Merchant Details", key: "password", label: "Odoo Password", type: "secret" },
  { section: "Merchant Details", key: "menuColor", label: "Menu Color", type: "color" },
  { section: "Merchant Details", key: "kdsDisplayColor", label: "Web KDS — Default Display Background", type: "color" },
  { section: "Merchant Details", key: "receiptLogo", label: "Receipt Logo (self-order screens)", type: "image", fileKey: "receiptLogo", removable: true },
  { section: "Merchant Details", key: "kdsDisplayImage", label: "Web KDS — Default Display Image", type: "image", fileKey: "kdsDisplayImage", removable: true },

  { section: "Menu & Loyalty", key: "enableMenuOrdering", label: "Enable Menu Ordering", type: "bool" },
  { section: "Menu & Loyalty", key: "enableMenuPagination", label: "Enable Menu Pagination", type: "bool" },
  { section: "Menu & Loyalty", key: "disableBackToMenus", label: "Disable Back to Menus", type: "bool" },
  { section: "Menu & Loyalty", key: "enableBranchProductsAlternatives", label: "Show Products Available at Other Branches", type: "bool" },
  { section: "Menu & Loyalty", key: "enableProductRecommendations", label: "Product Recommendations", type: "bool" },
  { section: "Menu & Loyalty", key: "enableCartTokenReapply", label: "Keep Coupon After Cart Changes", type: "bool" },
  { section: "Menu & Loyalty", key: "isLoyaltyActive", label: "Loyalty Program", type: "bool" },
  { section: "Menu & Loyalty", key: "isMembershipActive", label: "Membership Program", type: "bool" },
  { section: "Menu & Loyalty", key: "isWalletActive", label: "Customer Wallet", type: "bool" },
  { section: "Menu & Loyalty", key: "enablePosLoyaltyOrders", label: "Enable POS Loyalty Orders", type: "bool" },
  { section: "Menu & Loyalty", key: "autoCreatePosLoyaltyCustomers", label: "Auto-create POS Loyalty Customers", type: "bool" },
  { section: "Menu & Loyalty", key: "restoreRewardOnPosReturn", label: "Restore Reward on POS Return", type: "bool" },
  { section: "Menu & Loyalty", key: "coupons_enabled", label: "Enable Coupons Sync (from Merchant POS)", type: "bool" },

  { section: "Reporting", key: "hideAppOrdersAnalytics", label: "Hide App Orders Analytics report", type: "bool" },

  { section: "Pickup App Appearance", key: "sendTemplatesToPickupApp", label: "Enable Product Templates", type: "bool" },
  { section: "Pickup App Appearance", key: "branchesPlaceholderImage", label: "Branches Placeholder Image", type: "image", fileKey: "branchesPlaceholderImage" },
  { section: "Pickup App Appearance", key: "loyaltyCardLockedImage", label: "Loyalty Card Locked Image", type: "image", fileKey: "loyaltyCardLockedImage" },
  { section: "Pickup App Appearance", key: "loyaltyCardUnlockedImage", label: "Loyalty Card Unlocked Image", type: "image", fileKey: "loyaltyCardUnlockedImage" },
  { section: "Pickup App Appearance", key: "productPlaceholderImage", label: "Product Placeholder Image", type: "image", fileKey: "productPlaceholderImage" },
  { section: "Pickup App Appearance", key: "branchesListColumnsPickupApp", label: "Branches List Columns", type: "number" },

  { section: "Deliverect Integration", key: "clientId", label: "Client ID", type: "keepIfBlank" },
  { section: "Deliverect Integration", key: "clientSecret", label: "Client Secret", type: "keepIfBlank", mask: true },
  { section: "Deliverect Integration", key: "virtual_deliverect_sku", label: "Virtual Deliverect SKU", type: "bool" },

  { section: "Koinz Integration", key: "koinzPosId", label: "POS ID", type: "secret" },
  { section: "Koinz Integration", key: "koinzAccessKey", label: "Access Key", type: "secret" },
  { section: "Koinz Integration", key: "koinzMerchantKey", label: "Merchant Key", type: "secret" },

  { section: "Blend Integration", key: "blendBrandId", label: "Brand ID", type: "text" },
  { section: "Blend Integration", key: "blendMerchantEmail", label: "Merchant User Email", type: "text" },
  { section: "Blend Integration", key: "blendMerchantPassword", label: "Merchant User Password", type: "secret" },
  { section: "Blend Integration", key: "blendSyncAvailability", label: "Push Availability to Blend", type: "bool" },

  { section: "Google Maps", key: "googleMapsApiKey", label: "Distance Matrix API Key", type: "secret" },

  { section: "Payment Gateway", key: "paymentGateway", label: "Active Payment Gateway", type: "select", options: { moyasar: "Moyasar", telr: "Telr" } },

  { section: "Moyasar Payment", key: "moyasarPublishableKey", label: "Publishable API Key", type: "text" },
  { section: "Moyasar Payment", key: "moyasarSecretKey", label: "Secret API Key", type: "secret" },
  { section: "Moyasar Payment", key: "moyasarWebhookSecret", label: "Webhook Secret Token", type: "secret" },

  { section: "Telr Payment", key: "telrStoreId", label: "Store ID", type: "text" },
  { section: "Telr Payment", key: "telrApiKey", label: "Mobile SDK API Key", type: "secret" },
  { section: "Telr Payment", key: "telrRemoteAuthKey", label: "Remote API Key", type: "secret" },
  { section: "Telr Payment", key: "telrHostedAuthKey", label: "Hosted Payment Page Key", type: "secret" },
  { section: "Telr Payment", key: "telrWalletAuthKey", label: "Wallets Key", type: "secret" },
  { section: "Telr Payment", key: "applePayWebEnabled", label: "Apple Pay on web menu", type: "bool" },
  { section: "Telr Payment", key: "applePayMerchantId", label: "Apple Merchant ID", type: "text" },
  { section: "Telr Payment", key: "applePayDisplayName", label: "Name on the Apple Pay sheet", type: "text" },
  { section: "Telr Payment", key: "applePayIdentityCert", label: "Apple Pay Identity Certificate", type: "secret" },
  { section: "Telr Payment", key: "applePayIdentityKey", label: "Apple Pay Identity Private Key", type: "secret" },
  { section: "Telr Payment", key: "currency", label: "Currency", type: "keepIfBlank" },
  { section: "Telr Payment", key: "telrTestMode", label: "Telr Test Mode", type: "bool" },

  { section: "Online Orders", key: "carPickupActive", label: "Car Pickup", type: "bool" },
  { section: "Online Orders", key: "saveCards", label: "Save Cards", type: "bool" },
  { section: "Online Orders", key: "forceOnlineOrdersAsDelivery", label: "Force Online Orders as Delivery", type: "bool" },
  { section: "Online Orders", key: "blockOrderOnUnavailableItems", label: "Block Checkout on Unavailable Items", type: "bool" },
  { section: "Online Orders", key: "enableCustomerArrival", label: "Enable Customer Arrival", type: "bool" },
  { section: "Online Orders", key: "waitlistOtpRequired", label: "Require OTP for Waitlist (QR self-join)", type: "bool" },
  { section: "Online Orders", key: "notificationTarget", label: "Notification Destination", type: "select", options: { pos: "POS", kds: "KDS" } },
  { section: "Online Orders", key: "acceptNormalOrdersOnCreation", label: "Accept Normal Orders on Creation", type: "bool" },
  { section: "Online Orders", key: "customerFeedbackEnabled", label: "Customer Feedback QR on Receipts", type: "bool" },
  { section: "Online Orders", key: "customerFeedbackContactRequired", label: "Require Customer Details on the Feedback Form", type: "bool" },
  { section: "Printing", key: "posReceiptTemplatesEnabled", label: "POS receipt templates (Epson printers)", type: "bool" },
  { section: "Online Orders", key: "email_pending_orders_count", label: "Email Pending Orders Count", type: "number" },
  { section: "Online Orders", key: "staleDeviceOrdersAlertDays", label: "Stale Device Orders Alert (days)", type: "number" },
  { section: "Online Orders", key: "pendingOrdersCancellationMinutes", label: "Pending Orders Cancellation Minutes", type: "number" },
  { section: "Online Orders", key: "autoReadyMinutes", label: "Auto Ready Minutes", type: "number" },
  { section: "Online Orders", key: "autoDeliveredMinutes", label: "Auto Delivered Minutes", type: "number" },

  { section: "Marketing Limits", key: "pushCampaignWeeklyLimit", label: "Push campaign sends / week", type: "number", emptyLabel: "Default (3)" },
  { section: "Marketing Limits", key: "walletBroadcastWeeklyLimit", label: "Wallet offer announcements / week", type: "number", emptyLabel: "Default (3)" },

  { section: "Table Management", key: "timezone", label: "Timezone", type: "select" },
  { section: "Table Management", key: "staleTableWarningMinutes", label: "Stale Table Warning (minutes)", type: "number" },
];

const str = (v) => (v === null || v === undefined ? "" : String(v).trim());

/**
 * @param {object} args
 * @param {object} args.original      merchantData exactly as loaded (normalized)
 * @param {object} args.current       merchantData now
 * @param {object} args.files         { [fileKey]: File|null } newly picked images
 * @param {Set}    args.clearSecrets  secret keys marked for removal
 * @param {object} args.secretStatus  { [secretKey]: boolean } whether a value is stored now
 * @param {function} [args.customerLabel] customer id → display name
 * @returns {Array<{section,label,from,to,kind,sensitive,note}>}
 */
export function buildMerchantChanges({ original, current, files, clearSecrets, secretStatus, customerLabel }) {
  if (!original) return [];
  const rows = [];
  const push = (field, from, to, kind, note) =>
    rows.push({
      section: field.section,
      label: field.label,
      from,
      to,
      kind,
      note,
      sensitive: SENSITIVE_SECTIONS.has(field.section),
    });

  for (const field of FIELDS) {
    const before = original[field.key];
    const after = current[field.key];

    switch (field.type) {
      case "bool":
        if (!!before !== !!after) push(field, ON_OFF(before), ON_OFF(after), "change");
        break;

      case "number":
      case "text":
        if (str(before) !== str(after)) {
          const show = (v) => str(v) || field.emptyLabel || EMPTY;
          push(field, show(before), show(after), str(after) ? "change" : "remove");
        }
        break;

      case "color":
        if (str(before).toLowerCase() !== str(after).toLowerCase()) {
          push(field, str(before).toLowerCase(), str(after).toLowerCase(), "change");
        }
        break;

      case "select":
        if (str(before) !== str(after)) {
          const show = (v) =>
            field.key === "customer"
              ? (str(v) && customerLabel ? customerLabel(v) : str(v)) || EMPTY
              : field.options?.[str(v)] || str(v) || EMPTY;
          push(field, show(before), show(after), "change");
        }
        break;

      case "keepIfBlank":
        if (str(after) && str(after) !== str(before)) {
          push(
            field,
            field.mask ? (str(before) ? "Configured" : "Not set") : str(before) || EMPTY,
            field.mask ? "New value set" : str(after),
            str(before) ? "change" : "new"
          );
        }
        break;

      case "secret": {
        const stored = !!secretStatus?.[field.key];
        if (clearSecrets?.has(field.key)) {
          push(field, "Configured", "Will be removed", "remove", REMOVE_NOTES[field.key]);
        } else if (str(after)) {
          push(field, stored ? "Configured" : "Not set", "New value set", stored ? "change" : "new");
        }
        break;
      }

      case "image": {
        const file = files?.[field.fileKey];
        if (file instanceof File) {
          push(field, str(before) ? "Current image" : "None", `Replaced (${file.name})`, str(before) ? "change" : "new");
        } else if (field.removable && str(before) && !str(after)) {
          push(field, "Current image", "Removed", "remove");
        }
        break;
      }

      default:
        break;
    }
  }

  return rows;
}
